(() => {
  'use strict';

  const PRODUCT_SECTION_SELECTOR = '[id^="MainProduct-"]';
  const PRODUCT_FORM_SELECTOR = '.mp-form';
  const STICKY_BAR_SELECTOR = '[data-mobile-sticky-bar]';
  const STICKY_ADD_SELECTOR = '.mp-mobile-sticky-action--add';
  const INTERNAL_HEADER = 'X-KB-Custom-Cart';
  const GIFT_WRAP_FOR_PROPERTY = '_KB_GiftWrapFor';

  const controllers = new WeakMap();
  const swipeReadyStages = new WeakSet();
  let currentCart = null;
  let cartRequest = null;
  let initializationFrame = 0;

  const rootUrl = () => {
    const root = String(window.Shopify?.routes?.root || '/');
    return root.endsWith('/') ? root : `${root}/`;
  };

  const endpoint = (path) => `${rootUrl()}${String(path || '').replace(/^\//, '')}`;

  const trashIcon = () => `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4.5 7h15M9 7V4.8h6V7m-8.5 0 .8 12h9.4l.8-12M10 10.5v5.5M14 10.5v5.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path>
    </svg>`;

  const minusIcon = () => `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M6 12h12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>
    </svg>`;

  const plusIcon = () => `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 6v12M6 12h12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>
    </svg>`;

  const isGiftWrapItem = (item) => {
    const handle = String(item?.handle || '').toLowerCase();
    return Boolean(item?.properties?.[GIFT_WRAP_FOR_PROPERTY]) || handle === 'giftwrap' || handle === 'gift-wrap';
  };

  const visiblePropertyCount = (item) => {
    const properties = item?.properties && typeof item.properties === 'object' ? item.properties : {};
    return Object.keys(properties).filter((key) => key && !key.startsWith('_') && properties[key] !== '').length;
  };

  const matchingLines = (cart, variantId) => {
    if (!Array.isArray(cart?.items) || !variantId) return [];
    return cart.items.filter(
      (item) => !isGiftWrapItem(item) && Number(item.variant_id) === Number(variantId)
    );
  };

  const totalQuantity = (lines) => lines.reduce(
    (sum, line) => sum + Math.max(0, Number(line.quantity || 0)),
    0
  );

  const preferredLine = (lines) => {
    if (!lines.length) return null;
    return [...lines].sort((a, b) => visiblePropertyCount(a) - visiblePropertyCount(b))[0];
  };

  const lineForDecrease = (lines) => {
    const preferred = preferredLine(lines);
    if (preferred && Number(preferred.quantity || 0) > 0) return preferred;
    return lines.find((line) => Number(line.quantity || 0) > 0) || null;
  };

  const getControllerProductTitle = (controller) => {
    const title = controller?.section?.querySelector(
      'h1.product__title, .product__title h1, .mp-product-title, h1'
    )?.textContent;
    return String(title || '').replace(/\s+/g, ' ').trim() || 'this item';
  };

  const confirmRemoval = (controller) => {
    const confirmHandler = window.KBConfirmCartRemoval;
    const itemTitle = getControllerProductTitle(controller);
    if (typeof confirmHandler === 'function') return confirmHandler({ itemTitle });
    return Promise.resolve(window.confirm(`Remove ${itemTitle} from your cart?`));
  };

  function sectionSuffix(section) {
    return String(section?.id || '').replace(/^MainProduct-/, '');
  }

  function findStickyBar(section) {
    const suffix = sectionSuffix(section);
    const bars = Array.from(document.querySelectorAll(STICKY_BAR_SELECTOR));
    if (!bars.length) return null;

    if (suffix) {
      const expectedClass = `mp-mobile-sticky-actions-${suffix}`;
      const matched = bars.find((bar) => bar.classList.contains(expectedClass));
      if (matched) return matched;
    }

    return bars.length === 1 ? bars[0] : null;
  }

  function createController(section) {
    if (!section || controllers.has(section)) return controllers.get(section) || null;

    const form = section.querySelector(PRODUCT_FORM_SELECTOR);
    const variantInput = form?.querySelector('input[name="id"]');
    const stickyBar = findStickyBar(section);
    const stickyAddButton = stickyBar?.querySelector(STICKY_ADD_SELECTOR);

    if (!form || !variantInput || !stickyBar || !stickyAddButton) return null;

    const selector = document.createElement('div');
    selector.className = 'kb-product-sticky-quantity';
    selector.hidden = true;
    selector.setAttribute('role', 'group');
    selector.setAttribute('aria-label', 'Cart quantity');
    selector.innerHTML = `
      <button type="button" class="kb-product-sticky-quantity__action kb-product-sticky-quantity__action--decrease" data-kb-product-sticky-decrease aria-label="Decrease quantity">
        ${minusIcon()}
      </button>
      <span class="kb-product-sticky-quantity__value-wrap" aria-live="polite">
        <strong class="kb-product-sticky-quantity__value" data-kb-product-sticky-value>0</strong>
        <span class="kb-product-sticky-quantity__caption">In cart</span>
      </span>
      <button type="button" class="kb-product-sticky-quantity__action kb-product-sticky-quantity__action--increase" data-kb-product-sticky-increase aria-label="Increase quantity">
        ${plusIcon()}
      </button>`;

    stickyBar.insertBefore(selector, stickyAddButton);

    const controller = {
      section,
      form,
      variantInput,
      stickyBar,
      stickyAddButton,
      selector,
      decreaseButton: selector.querySelector('[data-kb-product-sticky-decrease]'),
      increaseButton: selector.querySelector('[data-kb-product-sticky-increase]'),
      valueElement: selector.querySelector('[data-kb-product-sticky-value]'),
      variantId: Number(variantInput.value || 0),
      lines: [],
      quantity: 0,
      pending: false,
    };

    controllers.set(section, controller);

    form.addEventListener('change', () => {
      window.setTimeout(() => syncControllerVariant(controller), 0);
      window.setTimeout(() => syncControllerVariant(controller), 80);
    });

    return controller;
  }

  function syncControllerVariant(controller) {
    if (!controller) return;
    const nextVariantId = Number(controller.variantInput?.value || 0);
    if (nextVariantId === controller.variantId) return;
    controller.variantId = nextVariantId;
    renderController(controller, currentCart);
  }

  function setPending(controller, pending) {
    controller.pending = Boolean(pending);
    controller.selector.classList.toggle('is-loading', controller.pending);
    controller.selector.setAttribute('aria-busy', controller.pending ? 'true' : 'false');
    controller.decreaseButton.disabled = controller.pending;
    controller.increaseButton.disabled = controller.pending;
  }

  function renderController(controller, cart) {
    if (!controller) return;

    syncControllerVariant(controller);
    const lines = matchingLines(cart, controller.variantId);
    const quantity = totalQuantity(lines);
    const inCart = quantity > 0;

    controller.lines = lines;
    controller.quantity = quantity;
    controller.stickyAddButton.classList.toggle('kb-product-sticky-add-hidden', inCart);
    controller.stickyAddButton.hidden = inCart;
    controller.stickyAddButton.setAttribute('aria-hidden', inCart ? 'true' : 'false');
    controller.stickyAddButton.tabIndex = inCart ? -1 : 0;
    controller.selector.hidden = !inCart;
    controller.stickyBar.classList.toggle('is-kb-product-in-cart', inCart);

    if (!inCart) {
      controller.valueElement.textContent = '0';
      return;
    }

    controller.valueElement.textContent = String(quantity);
    const isDelete = quantity === 1;
    controller.decreaseButton.classList.toggle('is-delete', isDelete);
    controller.decreaseButton.innerHTML = isDelete ? trashIcon() : minusIcon();
    controller.decreaseButton.setAttribute(
      'aria-label',
      isDelete ? 'Remove item from cart' : 'Decrease quantity'
    );
    controller.increaseButton.setAttribute('aria-label', 'Increase quantity');
  }

  function renderAll(cart) {
    if (!cart) return;
    currentCart = cart;
    document.querySelectorAll(PRODUCT_SECTION_SELECTOR).forEach((section) => {
      const controller = controllers.get(section) || createController(section);
      if (controller) renderController(controller, cart);
    });
  }

  async function loadCart({ force = false } = {}) {
    if (cartRequest && !force) return cartRequest;

    cartRequest = fetch(endpoint('cart.js'), {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        [INTERNAL_HEADER]: '1',
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to read cart quantities.');
        return response.json();
      })
      .then((cart) => {
        renderAll(cart);
        return cart;
      })
      .catch((error) => {
        console.error('[Kitchen Bazaar] Product sticky quantity sync failed.', error);
        return null;
      })
      .finally(() => {
        cartRequest = null;
      });

    return cartRequest;
  }

  function refreshOtherCartSurfaces(cart) {
    document.dispatchEvent(new CustomEvent('kb:cart:updated', { detail: { cart } }));

    const customCart = document.querySelector('kb-custom-cart');
    if (customCart?.requestRefresh) {
      customCart.requestRefresh({ open: false, toast: false, announce: false });
    }
  }

  async function changeQuantity(controller, direction) {
    if (!controller || controller.pending) return;

    syncControllerVariant(controller);
    const lines = matchingLines(currentCart, controller.variantId);
    const currentQuantity = totalQuantity(lines);

    if (!currentQuantity) {
      await loadCart({ force: true });
      return;
    }

    if (direction === 'decrease' && currentQuantity <= 1) {
      const confirmed = await confirmRemoval(controller);
      if (!confirmed) return;
    }

    const line = direction === 'increase' ? preferredLine(lines) : lineForDecrease(lines);
    const currentLineQuantity = Math.max(0, Number(line?.quantity || 0));
    const nextQuantity = direction === 'increase'
      ? currentLineQuantity + 1
      : Math.max(0, currentLineQuantity - 1);

    if (!line?.key) {
      await loadCart({ force: true });
      return;
    }

    setPending(controller, true);

    try {
      const response = await fetch(endpoint('cart/change.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          [INTERNAL_HEADER]: '1',
        },
        body: JSON.stringify({ id: line.key, quantity: nextQuantity }),
      });
      const cart = await response.json();
      if (!response.ok || cart.status) {
        throw new Error(cart.description || cart.message || 'Unable to update quantity.');
      }

      renderAll(cart);
      refreshOtherCartSurfaces(cart);
    } catch (error) {
      console.error('[Kitchen Bazaar] Product sticky quantity update failed.', error);
      document.querySelector('kb-custom-cart')?.announce?.(
        error.message || 'Unable to update quantity.'
      );
      await loadCart({ force: true });
    } finally {
      setPending(controller, false);
    }
  }


  function isSwipeExcludedTarget(target) {
    if (!target?.closest) return false;
    return Boolean(target.closest(
      'button, a, input, select, textarea, video, audio, iframe, model-viewer, [role="button"], [contenteditable="true"]'
    ));
  }

  function initializeGallerySwipe(root = document) {
    if (!root?.querySelectorAll) return;

    const stages = [];
    if (root.matches?.('.mp-stage')) stages.push(root);
    root.querySelectorAll('.mp-stage').forEach((stage) => stages.push(stage));

    stages.forEach((stage) => {
      if (swipeReadyStages.has(stage)) return;

      const section = stage.closest(PRODUCT_SECTION_SELECTOR);
      const previousButton = section?.querySelector('[data-gallery-prev]');
      const nextButton = section?.querySelector('[data-gallery-next]');
      const mediaItems = section?.querySelectorAll('[data-media-id]');
      if (!section || !previousButton || !nextButton || !mediaItems || mediaItems.length < 2) return;

      swipeReadyStages.add(stage);
      stage.classList.add('kb-product-gallery-swipe-ready');

      let startX = 0;
      let startY = 0;
      let startTime = 0;
      let tracking = false;

      stage.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1 || isSwipeExcludedTarget(event.target)) {
          tracking = false;
          return;
        }

        const touch = event.touches[0];
        startX = touch.clientX;
        startY = touch.clientY;
        startTime = Date.now();
        tracking = true;
      }, { passive: true });

      stage.addEventListener('touchcancel', () => {
        tracking = false;
      }, { passive: true });

      stage.addEventListener('touchend', (event) => {
        if (!tracking || event.changedTouches.length !== 1) return;
        tracking = false;

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - startX;
        const deltaY = touch.clientY - startY;
        const horizontalDistance = Math.abs(deltaX);
        const verticalDistance = Math.abs(deltaY);
        const threshold = Math.max(42, Math.min(76, stage.clientWidth * 0.12));
        const elapsed = Date.now() - startTime;

        if (elapsed > 900 || horizontalDistance < threshold || horizontalDistance <= verticalDistance * 1.2) return;

        if (deltaX < 0) nextButton.click();
        else previousButton.click();
      }, { passive: true });
    });
  }

  function initializeControllers(root = document) {
    if (!root?.querySelectorAll) return;

    const sections = [];
    if (root.matches?.(PRODUCT_SECTION_SELECTOR)) sections.push(root);
    root.querySelectorAll(PRODUCT_SECTION_SELECTOR).forEach((section) => sections.push(section));

    sections.forEach((section) => {
      const controller = controllers.get(section) || createController(section);
      if (controller && currentCart) renderController(controller, currentCart);
    });
  }

  function scheduleInitialize(root = document) {
    window.cancelAnimationFrame(initializationFrame);
    initializationFrame = window.requestAnimationFrame(() => {
      initializeControllers(root);
      initializeGallerySwipe(root);
      if (!currentCart) loadCart().catch(() => {});
    });
  }

  document.addEventListener('click', (event) => {
    const decreaseButton = event.target.closest('[data-kb-product-sticky-decrease]');
    const increaseButton = event.target.closest('[data-kb-product-sticky-increase]');
    const button = decreaseButton || increaseButton;
    if (!button) return;

    const selector = button.closest('.kb-product-sticky-quantity');
    const stickyBar = selector?.closest(STICKY_BAR_SELECTOR);
    const section = Array.from(document.querySelectorAll(PRODUCT_SECTION_SELECTOR)).find(
      (candidate) => findStickyBar(candidate) === stickyBar
    );
    const controller = section ? controllers.get(section) || createController(section) : null;
    if (!controller) return;

    event.preventDefault();
    event.stopPropagation();
    changeQuantity(controller, increaseButton ? 'increase' : 'decrease');
  }, true);

  document.addEventListener('kb:cart:updated', (event) => {
    const cart = event.detail?.cart;
    if (cart) renderAll(cart);
    else loadCart({ force: true }).catch(() => {});
  });

  ['cart:updated', 'cart:change', 'cart:refresh'].forEach((eventName) => {
    document.addEventListener(eventName, () => loadCart({ force: true }).catch(() => {}));
  });

  document.addEventListener('shopify:section:load', (event) => {
    scheduleInitialize(event.target || document);
    loadCart({ force: true }).catch(() => {});
  });

  window.addEventListener('pageshow', () => loadCart({ force: true }).catch(() => {}));
  window.addEventListener('popstate', () => {
    window.setTimeout(() => {
      initializeControllers(document);
      initializeGallerySwipe(document);
      loadCart({ force: true }).catch(() => {});
    }, 0);
  });

  const observer = new MutationObserver((mutations) => {
    const relevantChange = mutations.some((mutation) =>
      Array.from(mutation.addedNodes || []).some((node) =>
        node?.nodeType === 1 && (
          node.matches?.(STICKY_BAR_SELECTOR) ||
          node.querySelector?.(STICKY_BAR_SELECTOR) ||
          node.matches?.(PRODUCT_SECTION_SELECTOR) ||
          node.querySelector?.(PRODUCT_SECTION_SELECTOR)
        )
      )
    );

    if (relevantChange) scheduleInitialize(document);
  });

  function start() {
    initializeControllers(document);
    initializeGallerySwipe(document);
    loadCart({ force: true }).catch(() => {});
    observer.observe(document.documentElement, { childList: true, subtree: true });

    window.setTimeout(() => scheduleInitialize(document), 120);
    window.setTimeout(() => scheduleInitialize(document), 500);
    window.setTimeout(() => scheduleInitialize(document), 1200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
